/**
 * Phase 3 Flow Converter
 * Converts Phase 3 diagram data to React Flow format
 */

import type { Node, Edge } from '@xyflow/react';
import type { UnifiedDiagramData } from '@veodiagram/shared';

// ============= Type Mapping =============

/**
 * Maps backend node types to Phase 3 React Flow node types
 */
const NODE_TYPE_MAP: Record<string, string> = {
  // C4/System Context
  person: 'person',
  softwareSystem: 'softwareSystem',
  externalEntity: 'externalSystem',
  container: 'softwareSystem',
  component: 'softwareSystem',

  // Fallbacks
  start: 'trigger',
  end: 'resolution',
};

/**
 * Get the React Flow node type for a given backend node type
 */
function getNodeType(backendType: string): string {
  return NODE_TYPE_MAP[backendType] || 'meshService';
}

// ============= Layout Algorithms =============

interface LayoutOptions {
  direction: 'TB' | 'LR' | 'BT' | 'RL';
  nodeWidth: number;
  nodeHeight: number;
  horizontalSpacing: number;
  verticalSpacing: number;
}

const DEFAULT_LAYOUT: LayoutOptions = {
  direction: 'TB',
  nodeWidth: 180,
  nodeHeight: 80,
  horizontalSpacing: 50,
  verticalSpacing: 80,
};

/**
 * Simple hierarchical layout algorithm
 */
function calculateHierarchicalLayout(
  nodes: UnifiedDiagramData['nodes'],
  edges: UnifiedDiagramData['edges'],
  options: LayoutOptions = DEFAULT_LAYOUT
): Map<string, { x: number; y: number }> {
  const positions = new Map<string, { x: number; y: number }>();

  // Build adjacency list
  const children = new Map<string, string[]>();
  const parents = new Map<string, string[]>();
  const nodeIds = new Set(nodes.map(n => n.id));

  for (const edge of edges) {
    if (nodeIds.has(edge.source) && nodeIds.has(edge.target)) {
      if (!children.has(edge.source)) children.set(edge.source, []);
      children.get(edge.source)!.push(edge.target);

      if (!parents.has(edge.target)) parents.set(edge.target, []);
      parents.get(edge.target)!.push(edge.source);
    }
  }

  // Find root nodes (no parents)
  const roots = nodes.filter(n => !parents.has(n.id) || parents.get(n.id)!.length === 0);

  // BFS to assign levels
  const levels = new Map<string, number>();
  const visited = new Set<string>();
  const queue: { id: string; level: number }[] = [];

  for (const root of roots) {
    queue.push({ id: root.id, level: 0 });
  }

  // Handle disconnected nodes
  for (const node of nodes) {
    if (!roots.find(r => r.id === node.id) && !visited.has(node.id)) {
      queue.push({ id: node.id, level: 0 });
    }
  }

  while (queue.length > 0) {
    const { id, level } = queue.shift()!;
    if (visited.has(id)) continue;
    visited.add(id);
    levels.set(id, level);

    const nodeChildren = children.get(id) || [];
    for (const childId of nodeChildren) {
      if (!visited.has(childId)) {
        queue.push({ id: childId, level: level + 1 });
      }
    }
  }

  // Group nodes by level
  const levelGroups = new Map<number, string[]>();
  for (const [id, level] of levels) {
    if (!levelGroups.has(level)) levelGroups.set(level, []);
    levelGroups.get(level)!.push(id);
  }

  // Calculate positions
  const { nodeWidth, nodeHeight, horizontalSpacing, verticalSpacing, direction } = options;

  for (const [level, nodeIds] of levelGroups) {
    const totalWidth = nodeIds.length * nodeWidth + (nodeIds.length - 1) * horizontalSpacing;
    const startX = -totalWidth / 2;

    nodeIds.forEach((id, index) => {
      let x: number, y: number;

      if (direction === 'TB' || direction === 'BT') {
        x = startX + index * (nodeWidth + horizontalSpacing);
        y = level * (nodeHeight + verticalSpacing);
        if (direction === 'BT') y = -y;
      } else {
        y = startX + index * (nodeWidth + horizontalSpacing);
        x = level * (nodeHeight + verticalSpacing);
        if (direction === 'RL') x = -x;
      }

      positions.set(id, { x, y });
    });
  }

  return positions;
}

// ============= Edge Styling =============

interface EdgeStyleOptions {
  animated?: boolean;
  strokeColor?: string;
  strokeWidth?: number;
  strokeDasharray?: string;
}

/**
 * Get edge style based on edge type and properties
 */
function getEdgeStyle(edge: UnifiedDiagramData['edges'][0]): EdgeStyleOptions {
  const type = edge.type || 'default';

  const styleMap: Record<string, EdgeStyleOptions> = {
    // DDD relationships
    publishes: { strokeColor: '#22c55e', strokeWidth: 2 },
    subscribes: { strokeColor: '#f59e0b', strokeWidth: 2, animated: true },
    triggers: { strokeColor: '#3b82f6', strokeWidth: 2 },

    // HTTP and async
    http: { strokeColor: '#3b82f6', strokeWidth: 2 },
    async: { strokeColor: '#f59e0b', strokeWidth: 2, animated: true, strokeDasharray: '5,5' },

    // Default
    default: { strokeColor: '#64748b', strokeWidth: 2 },
    dependency: { strokeColor: '#94a3b8', strokeWidth: 1, strokeDasharray: '3,3' },
  };

  return styleMap[type] || styleMap.default;
}

// ============= Main Converter =============

export interface Phase3FlowData {
  nodes: Node[];
  edges: Edge[];
}

/**
 * Convert UnifiedDiagramData to React Flow format for Phase 3 diagrams
 */
export function convertPhase3ToReactFlow(
  data: UnifiedDiagramData,
  diagramType?: string
): Phase3FlowData {
  // Determine layout direction based on diagram type
  const layoutOptions: LayoutOptions = {
    ...DEFAULT_LAYOUT,
    direction: getLayoutDirection(diagramType),
  };

  // Calculate positions if not provided
  const calculatedPositions = calculateHierarchicalLayout(
    data.nodes,
    data.edges,
    layoutOptions
  );

  // Convert nodes
  const nodes: Node[] = data.nodes.map((node, index) => {
    const position = node.position || calculatedPositions.get(node.id) || { x: index * 200, y: 0 };

    return {
      id: node.id,
      type: getNodeType(node.type),
      position,
      data: {
        label: node.label,
        description: node.description,
        ...node.data,
        // Issue #175/#183: Evidence is already included via ...node.data spread
        // (API returns evidence in node.data.evidence, not node.evidence)
      },
    };
  });

  // Convert edges
  const edges: Edge[] = data.edges.map((edge) => {
    const style = getEdgeStyle(edge);

    return {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      label: edge.label,
      type: 'smoothstep',
      animated: style.animated || edge.animated,
      style: {
        stroke: style.strokeColor,
        strokeWidth: style.strokeWidth,
        strokeDasharray: style.strokeDasharray,
      },
      markerEnd: {
        type: 'arrowclosed' as const,
        color: style.strokeColor,
      },
      labelStyle: {
        fill: '#374151',
        fontWeight: 500,
        fontSize: 11,
      },
      labelBgStyle: {
        fill: 'white',
        fillOpacity: 0.9,
      },
    };
  });

  return { nodes, edges };
}

/**
 * Get layout direction based on diagram type
 */
function getLayoutDirection(diagramType?: string): 'TB' | 'LR' | 'BT' | 'RL' {
  switch (diagramType) {
    case 'systemContext':
      return 'TB'; // C4 diagrams typically top to bottom
    default:
      return 'TB';
  }
}

/**
 * Get diagram type label for display
 */
export function getPhase3DiagramLabel(diagramType: string): string {
  const labels: Record<string, string> = {
    systemContext: 'System Context (C4)',
  };

  return labels[diagramType] || 'Phase 3 Diagram';
}
